import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ShippingCalculator } from '../components/calculator';
import { Callout } from '../components/ui/Callout';
import { Icon } from '../components/ui/Icon';

enum ShippingStep {
  FORM = 0,
  LOADING = 1,
  RATES = 2,
  ADDRESS = 3,
}

const STEPS = ['Quote', 'Shipping', 'Payment', 'Confirmation'];

const ShippingSearchPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAlert, setShowAlert] = useState(false);
  const [showGetStartedHelp, setShowGetStartedHelp] = useState(!location.state);
  const [cachedShippingData, setCachedShippingData] = useState<any>(null);

  const currentStep = parseInt(searchParams.get('step') || '0', 10) as ShippingStep;
  // Map internal calculator steps (FORM/LOADING/RATES/ADDRESS) onto the visible
  // wizard step (Quote/Shipping/Payment/Confirmation).
  const wizardStep = Math.max(
    0,
    Math.min(currentStep === ShippingStep.LOADING ? 0 : currentStep === ShippingStep.RATES ? 0 : currentStep === ShippingStep.ADDRESS ? 1 : 0, STEPS.length - 1)
  );
  const progress = ((wizardStep + 1) / STEPS.length) * 100;

  const updateStep = useCallback(
    (step: ShippingStep, replaceHistory: boolean = false) => {
      const next = new URLSearchParams(searchParams);
      next.set('step', step.toString());
      setSearchParams(next, replaceHistory ? { replace: true } : undefined);
    },
    [searchParams, setSearchParams]
  );

  // Scroll to the very top whenever the wizard advances/regresses, so the
  // stepper + title are always visible when a new step opens.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const handleBack = () => {
    if (currentStep > 0) {
      updateStep((currentStep - 1) as ShippingStep);
    } else {
      navigate('/');
    }
  };

  return (
    <div className={`ship-wrap${currentStep > ShippingStep.FORM ? ' is-wide' : ''}`}>
      {/* Top stepper row */}
      <div className="ship-stepper-row">
        <div className="stepper stepper-lg">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className={`step ${i === wizardStep ? 'active' : i < wizardStep ? 'done' : ''}`}>
                <span className="step-num">{i < wizardStep ? '✓' : i + 1}</span>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className="step-line" />}
            </React.Fragment>
          ))}
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={handleBack}>
          <Icon name="arrowLeft" size={14} /> Back
        </button>
      </div>

      {/* Title with progress ring */}
      <header className="ship-title">
        <div>
          <span className="ship-kicker">Step {wizardStep + 1} of {STEPS.length}</span>
          <h1>
            Shipping <span className="ship-title-accent">calculator</span>
          </h1>
          <p>Calculate shipping costs and get quotes for your package.</p>
        </div>
        <div className="ship-progress-inline">
          <div
            className="ship-progress-ring"
            style={{ ['--p' as any]: progress }}
          >
            <svg viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15.9" className="bg" pathLength="100" />
              <circle cx="18" cy="18" r="15.9" className="fg" pathLength="100" />
            </svg>
            <span className="ship-progress-num">{Math.round(progress)}%</span>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
              Form progress
            </div>
            <div className="muted" style={{ fontSize: 11 }}>
              Keep going — you're almost there.
            </div>
          </div>
        </div>
      </header>

      {showAlert && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="info">
            <Icon name="info" size={16} />
            <span>
              Important update: pickup service is currently available only for{' '}
              <strong>selected cities</strong>.
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => setShowAlert(false)}
              style={{ marginInlineStart: 'auto' }}
              aria-label="Dismiss"
            >
              <Icon name="x" size={12} />
            </button>
          </Callout>
        </div>
      )}

      {showGetStartedHelp && (
        <div style={{ marginBottom: 20 }}>
          <Callout tone="info">
            <Icon name="info" size={16} />
            <span>
              <strong>Get started:</strong> Fill in the origin, destination, and parcel details
              below to see shipping options and rates.
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => setShowGetStartedHelp(false)}
              style={{ marginInlineStart: 'auto' }}
              aria-label="Dismiss"
            >
              <Icon name="x" size={12} />
            </button>
          </Callout>
        </div>
      )}

      <ShippingCalculator
        currentState={{
          originCountry: location.state?.origin || cachedShippingData?.origin,
          destinationCountry:
            location.state?.destination || cachedShippingData?.destination,
          Weight: location.state?.weight || cachedShippingData?.weight,
          dimensions: location.state?.dimensions || cachedShippingData?.dimensions,
          unitSystem: location.state?.unitSystem || cachedShippingData?.unitSystem,
          shippingType: location.state?.shippingType || cachedShippingData?.shippingType,
          envelopeSize: location.state?.envelopeSize || cachedShippingData?.envelopeSize,
          shipmentType: location.state?.shipmentType || cachedShippingData?.shipmentType,
          parcels: location.state?.parcels || cachedShippingData?.parcels,
          documents: location.state?.documents || cachedShippingData?.documents,
          autoGetRates:
            !!location.state?.origin && !!location.state?.destination,
          cachedRates: cachedShippingData?.rates,
          selectedRateId: cachedShippingData?.selectedRateId,
          selectedSourceAddress: cachedShippingData?.selectedSourceAddress,
          selectedDestinationAddress:
            cachedShippingData?.selectedDestinationAddress,
        }}
        onStepChange={updateStep}
        currentStep={currentStep}
        onCacheData={setCachedShippingData}
      />
    </div>
  );
};

export default ShippingSearchPage;
